// LEN-213 (designer D1): the AI config catalogue and the live values with their sources
// (Lender.Optimus.Ai AiConfigController). D2: the settings slots of a process, step, agent or
// skill, read verbatim, validated as a draft and saved (a save validates first; 422 when invalid).
const SETTINGS_ENTITY_SEGMENT = {
  process: 'processes',
  step: 'steps',
  agent: 'agents',
  skill: 'skills',
};

const getSettingsUrl = ({ entityType, entityId }) =>
  `/api/ai-config/${SETTINGS_ENTITY_SEGMENT[entityType]}/${entityId}/settings`;

export class AiConfigApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getCatalogue({ signal } = {}) {
    return this.#http.get('/api/ai-config/catalogue', { signal });
  }

  async getProcessResolved({ processId, stepId = null, signal }) {
    return this.#http.get(`/api/ai-config/processes/${processId}/resolved`, {
      params: { stepId },
      signal,
    });
  }

  async getStepEffectiveTools({ processId, stepId, signal }) {
    return this.#http.get(`/api/ai-config/processes/${processId}/steps/${stepId}/effective-tools`, {
      signal,
    });
  }

  async getAgentResolved({ agentKey, processId = null, stepId = null, signal }) {
    return this.#http.get(`/api/ai-config/agents/${encodeURIComponent(agentKey)}/resolved`, {
      params: { processId, stepId },
      signal,
    });
  }

  async getSkillResolved({ skillKey, signal }) {
    return this.#http.get(`/api/ai-config/skills/${encodeURIComponent(skillKey)}/resolved`, {
      signal,
    });
  }

  async getSettings({ entityType, entityId, signal }) {
    return this.#http.get(getSettingsUrl({ entityType, entityId }), { signal });
  }

  async getSettingsRevisions({ entityType, entityId, signal }) {
    return this.#http.get(`${getSettingsUrl({ entityType, entityId })}/revisions`, { signal });
  }

  async validateSettings({ entityType, entityId, settingsJson, processId = null }) {
    return this.#http.post(`${getSettingsUrl({ entityType, entityId })}/validate`, {
      settingsJson,
      processId,
    });
  }

  async saveSettings({
    entityType,
    entityId,
    settingsJson,
    expectedRevision,
    expectedSha256 = null,
    note = null,
  }) {
    return this.#http.put(getSettingsUrl({ entityType, entityId }), {
      settingsJson,
      expectedRevision,
      expectedSha256,
      note,
    });
  }

  // LEN-213 (step phases): a step's ordered phases, their revisions, a draft's validation and
  // the save (validates first; refuses a draft made on an older revision or text).
  async getStepPhases({ stepId, signal }) {
    return this.#http.get(`/api/ai-config/steps/${stepId}/phases`, { signal });
  }

  async getStepPhasesRevisions({ stepId, signal }) {
    return this.#http.get(`/api/ai-config/steps/${stepId}/phases/revisions`, { signal });
  }

  async validateStepPhases({ stepId, phases }) {
    return this.#http.post(`/api/ai-config/steps/${stepId}/phases/validate`, { phases });
  }

  async saveStepPhases({ stepId, phases, expectedRevision, expectedSha256 = null, note = null }) {
    return this.#http.put(`/api/ai-config/steps/${stepId}/phases`, {
      phases,
      expectedRevision,
      expectedSha256,
      note,
    });
  }

  // LEN-213 (phase check targeting): where a step's checks run among its phases (the step slot's
  // `phaseChecks` block): the view, a draft's validation and the save (validates first; refuses a
  // draft made on an older slot revision or text).
  async getStepPhaseChecks({ stepId, signal }) {
    return this.#http.get(`/api/ai-config/steps/${stepId}/phase-checks`, { signal });
  }

  async validateStepPhaseChecks({ stepId, targets }) {
    return this.#http.post(`/api/ai-config/steps/${stepId}/phase-checks/validate`, { targets });
  }

  async saveStepPhaseChecks({
    stepId,
    targets,
    expectedRevision,
    expectedSha256 = null,
    note = null,
  }) {
    return this.#http.put(`/api/ai-config/steps/${stepId}/phase-checks`, {
      targets,
      expectedRevision,
      expectedSha256,
      note,
    });
  }

  async validateSkillContributions({ skillId, contributionsJson }) {
    return this.#http.post(`/api/ai-config/skills/${skillId}/check-contributions/validate`, {
      settingsJson: contributionsJson,
    });
  }
}
